import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChatIcon, MagicWandIcon, SaveIcon } from "../icons";
import {
  useAssistance,
  useChat,
  useLocation,
  usePlugin,
  useVehicle,
  useView,
} from "../../../hooks";
import { MagicButton, NavigationButton } from "../buttons";
import { useFake } from "../../../../hooks";
import { Trans, useTranslation } from "react-i18next";
import { AssistanceCategory } from "../../../enums";
import moment from "moment/min/moment-with-locales";
import { MdFiberManualRecord } from "react-icons/md";
import HazardLightsIcon from "../icons/HazardLightsIcon";
import UnlockDoorsIcon from "../icons/UnlockDoorsIcon";
import SettingsIcon from "../icons/SettingsIcon";

/**
 * Renders the plugin navigation
 * 
 * @returns The plugin navigation
 */
const PluginNavigation = () => {
  const { t, i18n: { language } } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const { assistance } = useAssistance();
  const { onPluginScreenshot } = usePlugin();
  const { isChatOpen, toggleChat, isSettingsOpen } = useView();
  const { vehicle, isHazardLightsOn, toggleHazardLights, unlockDoors, occupantsCount, kidsCount } =
    useVehicle();
  const location = useLocation();
  const { onChatExport, unreadMessagesLength } = useChat();
  const { onMagicPaste } = useFake();
  const [isBlinking, setIsBlinking] = useState(true);
  const locale = useMemo(() => language.slice(0, 2), [language]);

  /**
   * Handles the magic paste confirm event
   */
  const onMagicPasteConfirm = useCallback(() => {
    const names = assistance.driver_name.split(" ");
    const firstName = names[0];
    const lastName = names.slice(1).join(" ");
    const transmissionType =
      vehicle?.Vehicle?.Powertrain?.Transmission?.Type?.toLowerCase();
    const isElectric =
      !!vehicle?.Vehicle?.Powertrain?.TractionBattery?.Charging?.Range;
    const isGas = !!vehicle?.Vehicle?.Powertrain?.FuelSystem?.Range;
    const fields = assistance?.category !== "ecall" ? Object.entries({
      firstName,
      lastName,
      phoneNumber: assistance?.driver_phone_number,
      streetNumber: location?.currentAddress?.AddressNumber,
      streetName: location?.currentAddress?.Street,
      interventionPostalCode: location?.currentAddress?.PostalCode,
      interventionCity: location?.currentAddress?.Municipality,
      interventionCountry: location?.currentAddress?.Country,
      adultsNumber: occupantsCount - kidsCount,
      childrenNumber: kidsCount,
      licensePlate: assistance?.license_plate,
      serialNumber: vehicle?.Vehicle?.VehicleIdentification?.VIN,
      brand: assistance?.make,
      model: assistance?.model,
      mileage: vehicle?.Vehicle?.TraveledDistance,
      gearbox: transmissionType
        ? t(`plugin.gearBox.type.${transmissionType}`)
        : "",
      energy:
        isGas && isElectric
          ? t("plugin.fuelType.hybrid")
          : isElectric
            ? t("plugin.fuelType.electric")
            : t("plugin.fuelType.gas"),
    }) : Object.entries({
      phoneNumber: assistance?.driver_phone_number,
      lastName,
      firstName,
      streetNumber: location?.currentAddress?.AddressNumber,
      streetName: location?.currentAddress?.Street,
      interventionPostalCode: location?.currentAddress?.PostalCode,
      interventionCity: location?.currentAddress?.Municipality,
      interventionCountry: location?.currentAddress?.Country,
      ai: assistance?.genAI?.full_response,
    });

    onMagicPaste(fields);
    onClose();
  }, [assistance, location, onClose, onMagicPaste, vehicle, t, occupantsCount, kidsCount]);

  /**
   * Handles the save click event
   */
  const onSaveClick = useCallback(() => {
    onChatExport();
    onPluginScreenshot();
  }, [onChatExport, onPluginScreenshot]);

  /**
   * Handles the toggle hazard lights click event
   */
  const remoteActions = useMemo(
    () => assistance?.available_remote_actions,
    [assistance],
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setIsBlinking((prev) => !prev);
    }, isBlinking ? 1600 : 2500);

    return () => {
      clearInterval(intervalId);
    }
  }, [isBlinking])

  return (
    <Card variant={"navigation"}>
      <CardBody padding={"16px"}>
        <VStack padding={0} gap={"14px"}>
          <MagicButton
            padding={0}
            icon={MagicWandIcon}
            onClick={onOpen}
            title={t("plugin.navigation.autofill")}
          />
          {remoteActions?.length > 0 && (
            <Box marginY={2} width={"50%"} height={"2px"} bgGradient={"radial(#D9DDDD, #D9DDDD00)"} borderRadius={"full"} />
          )}
          {remoteActions?.includes("warningLightsSwitchedOn") && (
            <NavigationButton
              padding={0}
              icon={HazardLightsIcon}
              color={isHazardLightsOn ? "#FF000099" : "inherit"}
              backgroundColor={isHazardLightsOn ? "#FF000033" : "inherit"}
              isSelected={isHazardLightsOn}
              onClick={toggleHazardLights}
              title={t("plugin.navigation.hazardLights")}
            />
          )}
          {remoteActions?.includes("doorsAreUnlocked") && (
            <NavigationButton
              padding={0}
              icon={UnlockDoorsIcon}
              onClick={unlockDoors}
              title={t("plugin.navigation.doors")}
            />
          )}
          <Box marginY={2} width={"50%"} height={"2px"} bgGradient={"radial(#D9DDDD, #D9DDDD00)"} borderRadius={"full"} />
          {assistance?.category === AssistanceCategory.ECALL ? null : (
            <NavigationButton
              padding={0}
              icon={ChatIcon}
              color={isChatOpen ? "#54C0E899" : "inherit"}
              backgroundColor={isChatOpen ? "#54C0E833" : "inherit"}
              isSelected={isChatOpen}
              onClick={toggleChat}
              title={t("plugin.navigation.messages")}
            >
              {unreadMessagesLength > 0 && (
                <Badge
                  backgroundColor={"#54C0E8"}
                  fontSize={"lg"}
                  position={"absolute"}
                  variant={"solid"}
                  top={-1}
                  right={-1}
                >
                  {unreadMessagesLength}
                </Badge>
              )}
            </NavigationButton>
          )}
          <NavigationButton
            padding={0}
            icon={SaveIcon}
            onClick={onSaveClick}
            title={t("plugin.navigation.save")}
          />
          <NavigationButton
            padding={0}
            icon={SettingsIcon}
            color={isSettingsOpen ? "#54C0E899" : "inherit"}
            backgroundColor={isSettingsOpen ? "#54C0E833" : "inherit"}
            isSelected={isSettingsOpen}
            onClick={() => { }}
            title={t("plugin.navigation.settings")}
          />
          <Tag size={"lg"} width={"80px"} borderWidth={"1px"} borderColor={"white"} marginTop={"10px"} color={isBlinking ? "red" : "white"} gap={0} transitionTimingFunction={"ease-in-out"} transitionDuration={isBlinking ? "0.8s" : "1s"}>
            <TagLeftIcon as={MdFiberManualRecord} boxSize={"15px"} margin={0} />
            <TagLabel marginTop={"2px"} fontSize={"16px"} fontWeight={500}>
              <Trans>plugin.navigation.live</Trans>
            </TagLabel>
          </Tag>
          <Text fontSize={"12px"} fontWeight={300}>
            <Trans>plugin.navigation.update</Trans>
          </Text>
          <Text fontSize={"12px"} fontWeight={300}>
            {moment(vehicle?.timestamp || 0).locale(locale).format("LTS")}
          </Text>
        </VStack>
      </CardBody>
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <Trans>plugin.magicPaste.title</Trans>
          </AlertDialogHeader>
          <AlertDialogBody>
            <Trans>plugin.magicPaste.description</Trans>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button colorScheme="gray" borderRadius={"10px"} ref={cancelRef} onClick={onClose}>
              <Trans>plugin.magicPaste.cancel</Trans>
            </Button>
            <Button colorScheme="red" backgroundColor={"#E57672"} borderRadius={"10px"} onClick={onMagicPasteConfirm} ml={3}>
              <Trans>plugin.magicPaste.confirm</Trans>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default React.memo(PluginNavigation);
